import { computedFrom } from 'aurelia-framework';
import { boundMethod } from 'autobind-decorator';
/**
 * This container implements the Menu component of the Design System while being integrated with
 * the app services and constants as data source.
 */
class MenuContainer {
  /**
   * @param {EventAggregator}  eventAggregator  To subscribe to route changing events.
   * @param {Router}           router           To get the current route when attached.
   * @param {AppConfiguration} appConfiguration To get the app title for the UI.
   * @param {AppRouting}       appRouting       To validate whether an option can be shown or not
   *                                            depending on its route settings.
   * @param {AppSession}       appSession       To subscribe to the session change events and
   *                                            toggle the menu visibility.
   * @param {AppNavigation}    NAVIGATION       To get all the navigation options.
   */
  constructor(
    eventAggregator,
    router,
    appConfiguration,
    appRouting,
    appSession,
    NAVIGATION,
  ) {
    'inject';

    /**
     * The app title for the UI.
     * @type {string}
     */
    this.appTitle = appConfiguration.get('appTitle');
    /**
     * When the container gets attached, this property will have the current route configuration.
     * @type {Object|RouteConfig}
     */
    this.currentRoute = {};
    /**
     * When the session changes, the container will detect if the user is logged in with a selected
     * account or not; and this property will either be the account avatar url or just the first
     * letter of the user name.
     * @type {string}
     */
    this.avatar = '';
    /**
     * When the session changes, the container will detect if the user is logged in with a selected
     * account or not; and this property, which will be the alternative text for the avatar, will
     * either be the accoutn name or the user name.
     * @type {String}
     */
    this.avatarText = '';
    /**
     * A local reference for the `NAVIGATION` constant. It's public as it will be used by the
     * template to send the routes to the menu component.
     * @type {AppNavigation}
     */
    this.NAVIGATION = NAVIGATION;
    /**
     * A local reference for the `eventAggregator` service.
     * @type {EventAggregator}
     * @access protected
     */
    this._eventAggregator = eventAggregator;
    /**
     * A local reference for the `router` service.
     * @type {Router}
     * @access protected
     */
    this._router = router;
    /**
     * A local reference for the `appRouting` service.
     * @type {AppRouting}
     * @access protected
     */
    this._appRouting = appRouting;
    /**
     * A local reference for the `appSession` service.
     * @type {AppSession}
     * @access protected
     */
    this._appSession = appSession;
    /**
     * Whether or not the user is authenticated; the value of the property will be updated every
     * time one of the session events is triggered (and intercepted by this container).
     * @type {boolean}
     * @access protected
     */
    this._authenticated = false;
    /**
     * Once the container is attached, this will be a list of {@link Subscription} objects for the
     * events the container is listening for.
     * @type {Array}
     * @access protected
     */
    this._subscriptions = [];
  }
  /**
   * When the container is attached, it will first detect the current route and then it will start
   * listening for the session and route change events.
   */
  attached() {
    // Get the current information.
    this.currentRoute = this._router.currentInstruction.config;
    // Add the event listeners.
    this._subscriptions = [
      this._eventAggregator.subscribe(
        'router:navigation:success',
        this._onRouteChange,
      ),
      this._appSession.onSessionCreated(this._onSessionChange),
      this._appSession.onSessionChange(this._onSessionChange),
    ];
    // Trigger the listeners to get the current state.
    this._onSessionChange();
  }
  /**
   * Disposes all the {@link Subscription}s when the container is detached.
   */
  detached() {
    this._subscriptions.forEach((subscription) => subscription.dispose());
    this._subscriptions = [];
  }
  /**
   * This method is being sent to the menu component and it's used to validate whether a user
   * can or can't see a route.
   * @param {RouteConfig} route The route configuration.
   * @return {boolean}
   */
  validateRoute(route) {
    let valid = true;
    if (route.settings) {
      valid = this._appRouting.validate(route.settings).allowed;
    }

    return valid;
  }
  /**
   * Tells the template whether to show or not the menu. It's value only changes when the route or
   * the session change.
   * By default, it shows the menu if the user is authenticated, but it can also be hidden if the
   * route specifies it (with the `hideMenu` setting).
   * @type {boolean}
   */
  @computedFrom('currentRoute', '_authenticated')
  get showMenu() {
    let show = this._authenticated;
    if (
      this.currentRoute &&
      this.currentRoute.settings &&
      this.currentRoute.settings.hideMenu === true
    ) {
      show = false;
    }

    return show;
  }
  /**
   * This method is called every time the navigation route changes. It updates the `currentRoute`
   * and manually checks for session changes.
   * @access protected
   */
  @boundMethod
  _onRouteChange(event) {
    this.currentRoute = event.instruction.config;
    this._onSessionChange();
  }
  /**
   * This method is called every time the session changes. It updates the property flag that tells
   * if the user is authenticated or not, and based on the session information, it sets the values
   * for `avatar` and `avatarText`.
   * @param {?Object} session If this is called from the SSO client after a successful
   *                          authentication, this object will contain the new session
   *                          information.
   * @access protected
   */
  @boundMethod
  _onSessionChange(session) {
    let sessionInformation;
    if (session && session.token) {
      // If this was called from the SSO client, get the information from the parameter.
      this._authenticated = true;
      sessionInformation = session;
    } else if (this._appSession.isAuthenticated()) {
      // If this was called manually, get the information from the SSO client.
      this._authenticated = true;
      sessionInformation = this._appSession.getSession();
    } else if (this._authenticated) {
      // Only change it if the value was different.
      this._authenticated = false;
    }

    // If the user is indeed authenticated, create the avatar.
    if (this._authenticated) {
      /**
       * If the user is authenticated with an account, use the account logo as avatar and its
       * name as text; otherwise, use the first letter of the user name as avatar.
       */
      if (sessionInformation.account) {
        this.avatarText = sessionInformation.account.name;
        this.avatar = sessionInformation.account.settings.avatar_url;
      } else {
        this.avatarText = sessionInformation.user.name;
        this.avatar = this.avatarText.substr(0, 1).toUpperCase();
      }
    }
  }
}

export { MenuContainer };
